import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { Package, Box, Cable, Wrench, RefreshCw, CheckCircle2, AlertCircle, QrCode, Undo2, X, Send } from "lucide-react";
import { toast } from "sonner";

// Return reasons for self-return requests
const RETURN_REASONS = [
  { value: "unused", label: "Unused - Not needed for current work" },
  { value: "defective", label: "Defective - Item not working" },
  { value: "wrong_item", label: "Wrong Item - Incorrect item issued" },
  { value: "project_complete", label: "Project Complete - Excess inventory" },
  { value: "other", label: "Other" },
];

const ITEM_CONDITIONS = [
  { value: "good", label: "Good - Working condition" },
  { value: "damaged", label: "Damaged - Physical damage" },
  { value: "defective", label: "Defective - Not functional" },
];

export default function MyInventoryPage() {
  const { user } = useAuth();
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [serialToValidate, setSerialToValidate] = useState("");
  const [validationResult, setValidationResult] = useState(null);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [issuedItems, setIssuedItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const [invRes, itemsRes, whRes] = await Promise.all([
        api.get("/inventory-issuance/my-inventory"),
        api.get("/inventory-issuance/my-issued-items"),
        api.get("/warehouses")
      ]);
      setInventory(invRes.data);
      setIssuedItems(itemsRes.data.items || []);
      setWarehouses(whRes.data || []);
    } catch (e) {
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInventory(); }, []);

  const handleValidateSerial = async (e) => {
    e.preventDefault();
    if (!serialToValidate.trim()) return;
    
    setValidating(true);
    setValidationResult(null);
    try {
      const res = await api.get(`/inventory-issuance/validate-serial/${serialToValidate.trim()}`);
      setValidationResult(res.data);
    } catch (e) {
      setValidationResult({ valid: false, message: e.response?.data?.detail || "Validation failed" });
    } finally {
      setValidating(false);
    }
  };

  // Toggle item selection for return
  const toggleItemForReturn = (item) => {
    const isSelected = selectedItems.find(i => i.id === item.id);
    if (isSelected) {
      setSelectedItems(selectedItems.filter(i => i.id !== item.id));
    } else {
      setSelectedItems([...selectedItems, { 
        ...item, 
        return_reason: "unused", 
        condition: "good" 
      }]);
    }
  };

  const updateItemReturnInfo = (itemId, field, value) => {
    setSelectedItems(selectedItems.map(i => 
      i.id === itemId ? { ...i, [field]: value } : i
    ));
  };

  const summary = inventory?.summary || {};
  const inv = inventory?.inventory || {};

  // Count of returnable items (issued but not consumed)
  const returnableItemsCount = issuedItems.filter(i => i.status === "issued").length;

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="my-inventory-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>My Inventory</h1>
          <p className="text-slate-500 text-sm">Materials issued to you for installation</p>
        </div>
        <div className="flex gap-2">
          {returnableItemsCount > 0 && (
            <button 
              onClick={() => setShowReturnModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-sm text-sm font-bold hover:bg-red-600"
              data-testid="initiate-return-btn"
            >
              <Undo2 size={16} />
              Return Items ({returnableItemsCount})
            </button>
          )}
          <button onClick={fetchInventory} className="p-2 border border-slate-200 rounded-sm hover:bg-slate-50">
            <RefreshCw size={16} className={loading ? "animate-spin text-orange-500" : "text-slate-500"} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
          <RefreshCw size={24} className="mx-auto animate-spin text-orange-500 mb-2" />
          <p className="text-slate-500">Loading inventory...</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border-2 border-orange-200 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Package size={20} className="text-orange-500" />
                <span className="text-xs font-bold text-slate-500 uppercase">Meters</span>
              </div>
              <div className="text-3xl font-black text-orange-600">{summary.total_meters || 0}</div>
              <div className="text-xs text-slate-500">Available</div>
            </div>

            <div className="bg-white border-2 border-blue-200 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Box size={20} className="text-blue-500" />
                <span className="text-xs font-bold text-slate-500 uppercase">SMC Boxes</span>
              </div>
              <div className="text-3xl font-black text-blue-600">{summary.total_smc_boxes || 0}</div>
              <div className="text-xs text-slate-500">Available</div>
            </div>

            <div className="bg-white border-2 border-purple-200 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Cable size={20} className="text-purple-500" />
                <span className="text-xs font-bold text-slate-500 uppercase">Cable Types</span>
              </div>
              <div className="text-3xl font-black text-purple-600">{summary.cable_types || 0}</div>
              <div className="text-xs text-slate-500">In Stock</div>
            </div>

            <div className="bg-white border-2 border-green-200 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Wrench size={20} className="text-green-500" />
                <span className="text-xs font-bold text-slate-500 uppercase">Accessories</span>
              </div>
              <div className="text-3xl font-black text-green-600">{summary.accessory_types || 0}</div>
              <div className="text-xs text-slate-500">Types</div>
            </div>
          </div>

          {/* Serial Validation */}
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
              <QrCode size={18} className="text-orange-500" />
              Validate Serial Before Installation
            </h3>
            <form onSubmit={handleValidateSerial} className="flex gap-2">
              <input
                type="text"
                placeholder="Enter meter serial number..."
                className="field-input flex-1"
                value={serialToValidate}
                onChange={e => setSerialToValidate(e.target.value)}
                data-testid="serial-input"
              />
              <button 
                type="submit" 
                disabled={validating}
                className="px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold hover:bg-orange-600 disabled:opacity-60"
                data-testid="validate-btn"
              >
                {validating ? "Checking..." : "Validate"}
              </button>
            </form>
            {validationResult && (
              <div className={`mt-3 p-3 rounded-sm flex items-start gap-2 ${validationResult.valid ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                {validationResult.valid ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
                <div>
                  <div className="font-semibold">{validationResult.valid ? "Valid" : "Invalid"}</div>
                  <div className="text-sm">{validationResult.message}</div>
                </div>
              </div>
            )}
          </div>

          {/* Meters List */}
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-slate-800">Meters ({inv.meters?.length || 0})</h3>
              <span className="text-xs text-slate-500">Serial numbers issued to you</span>
            </div>
            <div className="p-4">
              {inv.meters?.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Package size={32} className="mx-auto mb-2" />
                  <p>No meters currently issued to you</p>
                  <p className="text-xs">Contact your supervisor for inventory</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {inv.meters?.map((item, i) => (
                    <div key={i} className={`p-3 border rounded-sm ${item.status === "consumed" ? "border-green-200 bg-green-50" : "border-slate-200"}`}>
                      <div className="font-mono text-sm font-bold text-slate-800">{item.serial_number}</div>
                      <div className="text-xs text-slate-500 mt-1">
                        Issued: {item.issued_date} • From: {item.issued_from}
                      </div>
                      {item.status === "consumed" && (
                        <div className="mt-2 flex items-center gap-1 text-green-600 text-xs">
                          <CheckCircle2 size={12} />
                          Installed on {item.installed_on_dt}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* SMC Boxes */}
          {inv.smc_boxes?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-sm">
              <div className="px-4 py-3 border-b border-slate-100">
                <h3 className="font-bold text-slate-800">SMC Boxes ({inv.smc_boxes?.length})</h3>
              </div>
              <div className="p-4">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {inv.smc_boxes?.map((item, i) => (
                    <div key={i} className="p-3 border border-slate-200 rounded-sm">
                      <div className="font-mono text-sm font-bold text-slate-800">{item.serial_number}</div>
                      <div className="text-xs text-slate-500 mt-1">
                        Issued: {item.issued_date}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Cables */}
          {inv.cables?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-sm">
              <div className="px-4 py-3 border-b border-slate-100">
                <h3 className="font-bold text-slate-800">Cables ({inv.cables?.length} types)</h3>
              </div>
              <div className="p-4">
                <div className="space-y-2">
                  {inv.cables?.map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-3 border border-slate-100 rounded-sm">
                      <div>
                        <span className="font-semibold text-slate-800">{item.material_code}</span>
                        <span className="text-xs text-slate-500 ml-2">({item.unit})</span>
                      </div>
                      <div className="font-bold text-purple-600">{item.quantity} {item.unit}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Self-Return Modal */}
      {showReturnModal && (
        <ReturnRequestModal
          issuedItems={issuedItems.filter(i => i.status === "issued")}
          warehouses={warehouses}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          toggleItemForReturn={toggleItemForReturn}
          updateItemReturnInfo={updateItemReturnInfo}
          onClose={() => {
            setShowReturnModal(false);
            setSelectedItems([]);
          }}
          onSuccess={() => {
            setShowReturnModal(false);
            setSelectedItems([]);
            fetchInventory();
          }}
        />
      )}
    </div>
  );
}

// =============================================================================
// RETURN REQUEST MODAL COMPONENT
// =============================================================================
function ReturnRequestModal({ 
  issuedItems, 
  warehouses, 
  selectedItems, 
  setSelectedItems, 
  toggleItemForReturn, 
  updateItemReturnInfo,
  onClose, 
  onSuccess 
}) {
  const [loading, setLoading] = useState(false);
  const [toWarehouseId, setToWarehouseId] = useState("");
  const [notes, setNotes] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!toWarehouseId) {
      toast.error("Please select a warehouse to return items to");
      return;
    }
    if (selectedItems.length === 0) {
      toast.error("Please select at least one item to return");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        items: selectedItems.map(item => ({
          inventory_item_id: item.id,
          serial_number: item.serial_number,
          item_type: item.item_type,
          condition: item.condition,
          return_reason: item.return_reason,
        })),
        to_warehouse_id: toWarehouseId,
        notes: notes,
      };

      await api.post("/inventory-issuance/returns", payload);
      toast.success("Return request submitted successfully!");
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit return request");
    } finally {
      setLoading(false);
    }
  };

  const toWarehouse = warehouses.find(w => w.id === toWarehouseId);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-[100] p-4 overflow-y-auto">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl my-8 animate-fade-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-red-500 to-red-600">
          <div>
            <h2 className="text-lg font-bold text-white">Return Items</h2>
            <p className="text-red-100 text-xs">Select items you want to return to warehouse</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Items Selection */}
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <div className="bg-slate-100 px-4 py-3">
              <h3 className="font-bold text-slate-700 text-sm flex items-center gap-2">
                <Package size={16} className="text-red-500" />
                Select Items to Return ({selectedItems.length} selected)
              </h3>
            </div>
            <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
              {issuedItems.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <Package size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No items available to return</p>
                </div>
              ) : issuedItems.map(item => {
                const isSelected = selectedItems.find(i => i.id === item.id);
                const selectedItem = selectedItems.find(i => i.id === item.id);
                return (
                  <div key={item.id} className={`p-3 ${isSelected ? "bg-red-50" : "hover:bg-slate-50"}`}>
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <div 
                        onClick={() => toggleItemForReturn(item)}
                        className={`flex-shrink-0 w-5 h-5 rounded border-2 cursor-pointer flex items-center justify-center mt-0.5 ${
                          isSelected ? "bg-red-500 border-red-500 text-white" : "border-slate-300"
                        }`}
                        data-testid={`select-item-${item.id}`}
                      >
                        {isSelected && <CheckCircle2 size={12} />}
                      </div>
                      
                      {/* Item Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-700">
                            {item.item_type?.replace("_", " ")}
                          </span>
                          <span className="font-mono text-sm font-semibold text-slate-700">{item.serial_number}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{item.make} {item.model}</p>
                        
                        {/* Reason & Condition (only shown when selected) */}
                        {isSelected && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            <select
                              className="field-select text-xs py-1 w-44"
                              value={selectedItem?.return_reason || "unused"}
                              onChange={e => updateItemReturnInfo(item.id, "return_reason", e.target.value)}
                              onClick={e => e.stopPropagation()}
                              data-testid={`reason-${item.id}`}
                            >
                              {RETURN_REASONS.map(r => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                              ))}
                            </select>
                            <select
                              className="field-select text-xs py-1 w-36"
                              value={selectedItem?.condition || "good"}
                              onChange={e => updateItemReturnInfo(item.id, "condition", e.target.value)}
                              onClick={e => e.stopPropagation()}
                              data-testid={`condition-${item.id}`}
                            >
                              {ITEM_CONDITIONS.map(c => (
                                <option key={c.value} value={c.value}>{c.label}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Return To Warehouse */}
          <div className="bg-slate-50 rounded-sm p-4 border border-slate-200">
            <label className="font-bold text-slate-700 text-sm mb-2 block">Return To Warehouse *</label>
            <select
              className="field-select"
              value={toWarehouseId}
              onChange={e => setToWarehouseId(e.target.value)}
              required
              data-testid="return-warehouse"
            >
              <option value="">Select Warehouse</option>
              {warehouses.map(wh => (
                <option key={wh.id} value={wh.id}>
                  {wh.code} - {wh.name} ({wh.circle_name})
                </option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="field-label">Notes (Optional)</label>
            <textarea
              className="field-input"
              rows={2}
              placeholder="Any additional notes..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
              data-testid="return-notes"
            />
          </div>

          {/* Summary & Actions */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-sm p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-slate-400 text-xs uppercase">Return Summary</div>
                <div className="flex items-center gap-4 mt-1">
                  <div>
                    <span className="text-slate-400 text-sm">Items: </span>
                    <span className="text-2xl font-black text-red-400">{selectedItems.length}</span>
                  </div>
                  {toWarehouse && (
                    <div className="text-sm text-slate-300">
                      → {toWarehouse.code}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2 border border-slate-600 text-slate-300 rounded-sm font-medium hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || selectedItems.length === 0 || !toWarehouseId}
                  className="px-5 py-2 bg-red-500 text-white rounded-sm font-bold hover:bg-red-600 disabled:opacity-60 flex items-center gap-2"
                  data-testid="submit-return"
                >
                  {loading ? "Submitting..." : (
                    <>
                      <Send size={16} />
                      Submit Return
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
